/**
 * Outil « Corde et tangente » — fonctions et tests de position (pur).
 *
 * Convexe sur un intervalle : chaque corde est AU-DESSUS de la courbe et la
 * courbe est au-dessus de chacune de ses tangentes (f″ ≥ 0). Concave : le
 * contraire. Un point d'inflexion sépare les deux (f″ change de signe, la
 * tangente traverse la courbe). Positions vérifiées par échantillonnage.
 */

import type { Viewport } from '../../ui/frame';

export type FnId = 'square' | 'exp' | 'ln' | 'cubic' | 'sin';

export interface Zone {
  from: number;
  to: number;
  kind: 'convex' | 'concave';
}

export interface ConvexFn {
  id: FnId;
  label: string;
  tex: string;
  d2tex: string;
  f: (x: number) => number;
  d1: (x: number) => number;
  d2: (x: number) => number;
  /** abscisses permises pour A et B */
  xMin: number;
  xMax: number;
  vp: Viewport;
  zones: Zone[];
  inflections: number[];
  /** inégalité de convexité classique */
  inequality: string;
}

export const FUNCS: readonly ConvexFn[] = [
  {
    id: 'square',
    label: 'x²',
    tex: 'x^2',
    d2tex: "f''(x) = 2",
    f: (x) => x * x,
    d1: (x) => 2 * x,
    d2: () => 2,
    xMin: -2.5,
    xMax: 2.5,
    vp: { xMin: -3, xMax: 3, yMin: -1.5, yMax: 6.5 },
    zones: [{ from: -Infinity, to: Infinity, kind: 'convex' }],
    inflections: [],
    inequality:
      'Convexe sur ℝ : x² ≥ 2ax − a² pour tous a et x (la parabole est au-dessus de ses tangentes).',
  },
  {
    id: 'exp',
    label: 'eˣ',
    tex: 'e^x',
    d2tex: "f''(x) = e^x",
    f: Math.exp,
    d1: Math.exp,
    d2: Math.exp,
    xMin: -2.5,
    xMax: 1.8,
    vp: { xMin: -3, xMax: 2.2, yMin: -1, yMax: 6.5 },
    zones: [{ from: -Infinity, to: Infinity, kind: 'convex' }],
    inflections: [],
    inequality: 'Convexe sur ℝ : eˣ ≥ 1 + x pour tout x (tangente en 0).',
  },
  {
    id: 'ln',
    label: 'ln x',
    tex: '\\ln x',
    d2tex: "f''(x) = -\\frac{1}{x^2}",
    f: Math.log,
    d1: (x) => 1 / x,
    d2: (x) => -1 / (x * x),
    xMin: 0.2,
    xMax: 6,
    vp: { xMin: -0.5, xMax: 6.5, yMin: -3, yMax: 3 },
    zones: [{ from: 0, to: Infinity, kind: 'concave' }],
    inflections: [],
    inequality: 'Concave sur ]0 ; +∞[ : ln x ≤ x − 1 pour tout x > 0 (tangente en 1).',
  },
  {
    id: 'cubic',
    label: 'x³ − 3x',
    tex: 'x^3 - 3x',
    d2tex: "f''(x) = 6x",
    f: (x) => x ** 3 - 3 * x,
    d1: (x) => 3 * x * x - 3,
    d2: (x) => 6 * x,
    xMin: -2.2,
    xMax: 2.2,
    vp: { xMin: -2.6, xMax: 2.6, yMin: -4.5, yMax: 4.5 },
    zones: [
      { from: -Infinity, to: 0, kind: 'concave' },
      { from: 0, to: Infinity, kind: 'convex' },
    ],
    inflections: [0],
    inequality:
      'f″(x) = 6x change de signe en 0 : concave avant, convexe après ; en 0, la tangente traverse la courbe.',
  },
  {
    id: 'sin',
    label: 'sin x',
    tex: '\\sin x',
    d2tex: "f''(x) = -\\sin x",
    f: Math.sin,
    d1: Math.cos,
    d2: (x) => -Math.sin(x),
    xMin: -3.2,
    xMax: 3.2,
    vp: { xMin: -3.5, xMax: 3.5, yMin: -2, yMax: 2 },
    zones: [
      { from: -Infinity, to: -Math.PI, kind: 'concave' },
      { from: -Math.PI, to: 0, kind: 'convex' },
      { from: 0, to: Math.PI, kind: 'concave' },
      { from: Math.PI, to: Infinity, kind: 'convex' },
    ],
    inflections: [-Math.PI, 0, Math.PI],
    inequality:
      'Concave sur [0 ; π] : sin x ≤ x pour x ≥ 0 (tangente en 0) ; en 0, la tangente traverse la courbe.',
  },
];

export function fnById(id: FnId): ConvexFn {
  return FUNCS.find((f) => f.id === id)!;
}

const N = 200;
const EPS = 1e-9;

/** la corde [AB] est-elle au-dessus de la courbe entre A et B, en dessous, ou la traverse-t-elle ? */
export function chordPosition(fn: ConvexFn, a: number, b: number): 'above' | 'below' | 'crosses' {
  const [lo, hi] = a < b ? [a, b] : [b, a];
  const fa = fn.f(lo);
  const fb = fn.f(hi);
  let above = true;
  let below = true;
  for (let i = 1; i < N; i++) {
    const t = lo + ((hi - lo) * i) / N;
    const chord = fa + ((fb - fa) * (t - lo)) / (hi - lo);
    const y = fn.f(t);
    if (y > chord + EPS) above = false;
    if (y < chord - EPS) below = false;
  }
  return above ? 'above' : below ? 'below' : 'crosses';
}

/**
 * Position de la courbe par rapport à sa tangente en a, sur toute la fenêtre
 * affichée (là où f est définie) :
 * au-dessus, en dessous, ou de part et d'autre (traverse en a si a est un
 * point d'inflexion, la recoupe ailleurs sinon).
 */
export function tangentPosition(fn: ConvexFn, a: number): 'curveAbove' | 'curveBelow' | 'crosses' {
  const fa = fn.f(a);
  const s = fn.d1(a);
  let above = true;
  let below = true;
  const { xMin, xMax } = fn.vp;
  for (let i = 0; i <= N; i++) {
    const t = xMin + ((xMax - xMin) * i) / N;
    const tan = fa + s * (t - a);
    const y = fn.f(t);
    if (!Number.isFinite(y)) continue;
    if (y < tan - EPS) above = false;
    if (y > tan + EPS) below = false;
  }
  return above ? 'curveAbove' : below ? 'curveBelow' : 'crosses';
}

/** les abscisses d'inflexion strictement entre a et b */
export function inflectionsBetween(fn: ConvexFn, a: number, b: number): number[] {
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return fn.inflections.filter((x) => x > lo && x < hi);
}

export function zoneAt(fn: ConvexFn, x: number): 'convex' | 'concave' | 'inflection' {
  if (fn.inflections.some((v) => Math.abs(v - x) < 1e-9)) return 'inflection';
  return fn.zones.find((z) => x >= z.from && x <= z.to)?.kind ?? 'convex';
}
